import { css } from '@emotion/css';

import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType.ts';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { TerminalProvider } from '../state/useParkingsFetch.ts';
import { Icon16 } from 'dg-web-shared/ui/icons/Icon.tsx';
import { Typo } from 'dg-web-shared/ui/typo.ts';
import { type JSX, ReactNode } from 'react';
import { Box } from '@mui/material';
import { SvgIcon } from 'dg-web-shared/ui/icons/SvgIcon.tsx';

export enum IdentificationType {
    BADGE = 'BADGE',
    LICENSE_PLATE = 'LICENSE_PLATE',
    QR_CODE = 'QR_CODE',
    TICKET = 'TICKET',
}

export const IdentificationLicensePlate = ({
    licensePlateNr,
    type,
    country,
    inlineMode,
}: {
    licensePlateNr: string;
    type?: LicensePlateType | null;
    country?: string | null;
    inlineMode: boolean;
}) => (
    <div
        className={css({
            display: 'flex',
            alignItems: 'center',
        })}
    >
        <div
            className={css(
                inlineMode
                    ? {
                          fontSize: '13px',
                          lineHeight: '16px',
                          letterSpacing: '0.01em',
                      }
                    : {
                          fontSize: '16px',
                          lineHeight: '22px',
                          ...Typo.robotoMedium,
                      },
            )}
        >
            {licensePlateNr}
        </div>
        {country && type
            ? [
                  <div
                      key="c"
                      className={css({
                          ...Typo.captionC2,
                          paddingLeft: '6px',
                      })}
                  >
                      {country}
                  </div>,
                  <div
                      key="t"
                      className={css({
                          paddingLeft: '6px',
                      })}
                  >
                      <IdentificationIcon16
                          identificationType={IdentificationType.LICENSE_PLATE}
                          licensePlateType={type}
                      />
                  </div>,
              ]
            : null}
    </div>
);

export function IdentificationBadge({
    badgeLabelNr,
    barrierGateProvider,
    rfidDecimal,
    inlineMode,
}: {
    badgeLabelNr: string;
    barrierGateProvider?: TerminalProvider | null;
    rfidDecimal: string | null;
    inlineMode: boolean;
}) {
    const decimalProviderLabel =
        barrierGateProvider === TerminalProvider.SKIDATA_CLOUD ||
        barrierGateProvider === TerminalProvider.SKIDATA_CLOUD_V7 ? (
            <Localized
                de="Skidata-Nr."
                fr="N° Skidata"
                it="Nr. Skidata"
                en="Skidata Nr"
            />
        ) : barrierGateProvider === TerminalProvider.SB ? (
            <Localized
                de="Scheidt & Bachmann-Nr."
                fr="N° Scheidt & Bachmann"
                it="Nr. Scheidt & Bachmann"
                en="Scheidt & Bachmann Nr"
            />
        ) : null;

    return (
        <>
            <div
                className={css({
                    display: 'flex',
                    alignItems: 'center',
                })}
            >
                <div
                    className={css(
                        inlineMode
                            ? {
                                  fontSize: '13px',
                                  lineHeight: '16px',
                                  letterSpacing: '0.01em',
                              }
                            : {
                                  fontSize: '16px',
                                  lineHeight: '22px',
                                  ...Typo.robotoMedium,
                              },
                    )}
                >
                    {badgeLabelNr}
                </div>

                <div
                    className={css({
                        paddingLeft: '6px',
                        position: 'relative',
                    })}
                >
                    <IdentificationIcon16
                        identificationType={IdentificationType.BADGE}
                        licensePlateType={null}
                    />
                </div>
            </div>

            {!inlineMode && rfidDecimal && decimalProviderLabel && (
                <div>
                    ({decimalProviderLabel} {rfidDecimal})
                </div>
            )}
        </>
    );
}

export function IdentificationQr({
    identificationQrCodeId,
}: {
    identificationQrCodeId: string;
}) {
    return (
        <div
            className={css({
                display: 'flex',
                alignItems: 'center',
            })}
        >
            <div className={css({ marginRight: '6px' })}>
                {identificationQrCodeId}
            </div>
            <IdentificationIcon16
                identificationType={IdentificationType.QR_CODE}
                licensePlateType={null}
            />
        </div>
    );
}

export function IdentificationIcon16({
    identificationType,
    licensePlateType,
}: {
    identificationType: IdentificationType | null;
    licensePlateType: LicensePlateType | null;
}) {
    if (identificationType === null) {
        return <>{EmDash}</>;
    }
    return (
        <Icon16
            icon={
                <IdentificationIconForType
                    identificationType={identificationType}
                    licensePlateType={licensePlateType}
                />
            }
        />
    );
}

function IdentificationIconForType({
    identificationType,
    licensePlateType,
}: {
    identificationType: IdentificationType;
    licensePlateType: LicensePlateType | null;
}): JSX.Element {
    switch (identificationType) {
        case IdentificationType.LICENSE_PLATE:
            switch (licensePlateType) {
                case LicensePlateType.CAR:
                    return Icons16.licensePlateType.car;
                case LicensePlateType.MOTORCYCLE:
                    return Icons16.licensePlateType.motorcycle;
                case null:
            }
            return Icons16.licensePlateType.car;
        case IdentificationType.BADGE:
            return Icons16.identifier.badge;
        case IdentificationType.QR_CODE:
            return Icons16.identifier.qrCode;
        case IdentificationType.TICKET:
            return Icons16.identifier.ticket;
    }
}

export function IdentificationBox({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
            }}
        >
            {children}
        </Box>
    );
}

export function IdentificationIcon({
    identificationType,
    licensePlateType,
}: {
    identificationType: IdentificationType;
    licensePlateType: LicensePlateType | null;
}) {
    return (
        <IdentificationIconBox>
            <SvgIcon
                icon={
                    <IdentificationIconForType
                        identificationType={identificationType}
                        licensePlateType={licensePlateType}
                    />
                }
                width={IDENTIFICATION_ICON_SIZE}
                height={IDENTIFICATION_ICON_SIZE}
            />
        </IdentificationIconBox>
    );
}

export function IdentificationIconBox({ children }: { children: ReactNode }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '22px',
            }}
        >
            {children}
        </Box>
    );
}

export const IDENTIFICATION_ICON_SIZE = 17;
